import { Observable } from 'rxjs';
import { GatewayBilletRequest, ITAU_GATEWAY_BANK_CODE } from '../../../models/itau-gateway-config';

/** O que a coluna Boleto da lista de faturas mostra para o boleto Itaú pedido pelo gateway. */
export interface GatewayBilletState {
  alertKind?: 'REFUSED' | 'PENDING';
  alertText?: string;
  /** Id do boleto emitido pelo gateway que ficou sem PDF (mostra "Gerar PDF"). */
  retryPdfBilletId?: number;
}

function hasActiveBillet(invoice: any): boolean {
  const billet = invoice ? invoice.activeBillet : null;
  return !!billet && billet.active === true;
}

/** O backend grava document_url " " quando o PDF do gateway falha. */
function isBlank(value: any): boolean {
  return value === null || value === undefined || String(value).trim().length === 0;
}

/**
 * Situação para uma fatura, a partir do pedido de boleto do gateway:
 * - REFUSED, sem boleto ativo: alerta "Boleto recusado pelo banco: <motivo>";
 * - PENDING, sem boleto ativo: "Boleto em emissão pelo banco";
 * - ISSUED, com boleto ativo do Itaú sem PDF: botão "Gerar PDF".
 * Fatura cancelada não mostra nada; paga não mostra recusa nem PDF.
 */
export function gatewayBilletState(invoice: any, request: GatewayBilletRequest): GatewayBilletState | null {
  if (!invoice || !request || invoice.status === 'CANCELED') {
    return null;
  }
  const status = String(request.status || '').toUpperCase();
  if (!hasActiveBillet(invoice)) {
    if (status === 'REFUSED' && invoice.status !== 'PAID') {
      const reason = (request.lastError || '').trim() || 'motivo não informado';
      return { alertKind: 'REFUSED', alertText: 'Boleto recusado pelo banco: ' + reason };
    }
    if (status === 'PENDING' && invoice.status !== 'PAID') {
      return { alertKind: 'PENDING', alertText: 'Boleto em emissão pelo banco' };
    }
    return null;
  }
  const billet = invoice.activeBillet;
  if (status === 'ISSUED' && Number(billet.bankNumber) === ITAU_GATEWAY_BANK_CODE && billet.id != null
    && billet.paid !== true && isBlank(billet.documentUrl)) {
    return { retryPdfBilletId: billet.id };
  }
  return null;
}

/** Monta o mapa invoiceId → situação para as faturas da página (pedido de maior tentativa por fatura). */
export function buildGatewayBilletStates(invoices: any[], requests: GatewayBilletRequest[]): { [invoiceId: number]: GatewayBilletState } {
  const latest = new Map<number, GatewayBilletRequest>();
  (requests || []).forEach((request) => {
    if (!request || request.invoiceId == null) {
      return;
    }
    const current = latest.get(request.invoiceId);
    if (!current || (request.attempt || 0) >= (current.attempt || 0)) {
      latest.set(request.invoiceId, request);
    }
  });
  const states: { [invoiceId: number]: GatewayBilletState } = {};
  (invoices || []).forEach((invoice) => {
    const state = invoice && invoice.id != null ? gatewayBilletState(invoice, latest.get(invoice.id)) : null;
    if (state) {
      states[invoice.id] = state;
    }
  });
  return states;
}

/** O que o loader precisa do ItauBankGatewayService. */
export interface GatewayBilletRequestSource {
  findBilletRequests(invoiceIds: number[]): Observable<GatewayBilletRequest[]>;
}

/**
 * Carrega a situação do boleto Itaú pelo gateway para a página de faturas: uma chamada por página;
 * falha, 404 ou vazio = nada a mostrar; resposta de uma página anterior é descartada.
 */
export class GatewayBilletStatesLoader {
  states: { [invoiceId: number]: GatewayBilletState } = {};
  private seq = 0;

  constructor(private source: GatewayBilletRequestSource) {
  }

  load(invoices: any[]): void {
    const seq = ++this.seq;
    this.states = {};
    const page = invoices || [];
    this.source.findBilletRequests(page.map((invoice) => invoice && invoice.id)).subscribe({
      next: (requests) => {
        if (seq === this.seq) {
          this.states = buildGatewayBilletStates(page, requests);
        }
      },
      error: () => {}
    });
  }
}
